import { useState, useEffect, useCallback } from 'react';
import sensorService from '../services/sensorService';

const useSensorData = (autoRefresh = true, refreshInterval = 30000) => {
  const [dashboardData, setDashboardData] = useState(null);
  const [historicalData, setHistoricalData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);

  const fetchDashboardData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      const [dashboardResponse, historicalResponse] = await Promise.all([
        sensorService.getDashboardData(),
        sensorService.getPastDaySensorData(),
      ]);
      
      if (dashboardResponse.success) {
        setDashboardData(dashboardResponse.data);
        setLastUpdated(new Date());
      } else {
        throw new Error(dashboardResponse.message || 'Failed to fetch dashboard data');
      }

      if (historicalResponse.success) {
        setHistoricalData(historicalResponse.data);
      } else {
        // Non-critical error, so we just log it
        console.warn(historicalResponse.message || 'Failed to fetch historical data');
      }

    } catch (err) {
      console.error('Error fetching sensor data:', err);
      setError(err.message || 'Failed to fetch sensor data');
    } finally {
      setLoading(false);
    }
  }, []);

  const refreshData = useCallback(() => {
    fetchDashboardData();
  }, [fetchDashboardData]);

  // Initial data fetch
  useEffect(() => {
    fetchDashboardData();
  }, [fetchDashboardData]);

  // Auto-refresh functionality
  useEffect(() => {
    if (!autoRefresh) return;

    const interval = setInterval(() => {
      fetchDashboardData();
    }, refreshInterval);

    return () => clearInterval(interval);
  }, [autoRefresh, refreshInterval, fetchDashboardData]);

  return {
    dashboardData,
    historicalData,
    loading,
    error,
    lastUpdated,
    refreshData,
  };
};

export default useSensorData;
